<template>
  <b-card no-body>
    <b-card-header>
      <b-card-title>{{ $t('dashboard.financialSummary') }}</b-card-title>
      <div class="d-flex align-items-center">
        <b-form-select
          v-model="selectedPeriod"
          :options="periodOptions"
          size="sm"
          class="mr-1"
          style="width: auto;"
          @change="onPeriodChange"
        />
        <b-form-select
          v-if="selectedPeriod === 'quarter'"
          v-model="selectedQuarter"
          :options="quarterOptions"
          size="sm"
          class="mr-1"
          style="width: auto;"
          @change="onFilterChange"
        />
        <b-form-select
          v-if="showYearSelector"
          v-model="selectedYear"
          :options="yearOptions"
          size="sm"
          class="mr-1"
          style="width: auto;"
          @change="onFilterChange"
        />
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="outline-secondary"
          size="sm"
          class="mr-1"
          @click="showCustomDateModal = true"
        >
          <feather-icon
            icon="CalendarIcon"
            size="16"
          />
        </b-button>
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          size="sm"
          :disabled="isLoading"
          @click="refreshData"
        >
          <feather-icon
            icon="RefreshCwIcon"
            :class="{ 'animate-spin': isLoading }"
          />
        </b-button>
      </div>
    </b-card-header>
    <b-card-body v-if="financial">
      <b-row>
        <!-- Revenue Summary -->
        <b-col
          lg="4"
          md="12"
          class="mb-2"
        >
          <b-card
            bg-variant="success"
            text-variant="white"
          >
            <h3 class="text-white mb-1">
              {{ formatCurrency(financial.total_revenue) }}
            </h3>
            <span>{{ $t('dashboard.totalRevenue') }}</span>
          </b-card>
        </b-col>

        <b-col
          lg="4"
          md="12"
          class="mb-2"
        >
          <b-card
            bg-variant="warning"
            text-variant="white"
          >
            <h3 class="text-white mb-1">
              {{ formatCurrency(financial.pending_amount) }}
            </h3>
            <span>{{ $t('dashboard.pendingAmount') }}</span>
          </b-card>
        </b-col>

        <b-col
          lg="4"
          md="12"
          class="mb-2"
        >
          <b-card
            bg-variant="info"
            text-variant="white"
          >
            <h3 class="text-white mb-1">
              {{ financial.budget_analysis.utilization_percentage }}%
            </h3>
            <span>{{ $t('dashboard.budgetUtilization') }}</span>
          </b-card>
        </b-col>

        <!-- Transactions by Status -->
        <b-col
          lg="6"
          md="12"
          class="mt-2"
        >
          <h5 class="mb-1">
            {{ $t('dashboard.transactionsByStatus') }}
          </h5>
          <b-list-group>
            <b-list-group-item
              v-for="(data, status) in financial.transactions_by_status"
              :key="status"
              class="d-flex justify-content-between align-items-center"
            >
              <div>
                <b-badge
                  :variant="getStatusVariant(status)"
                  class="mr-1"
                >
                  {{ $t(`transaction.status.${status}`) }}
                </b-badge>
                <span>{{ data.count }} {{ $t('dashboard.transactions') }}</span>
              </div>
              <strong>{{ formatCurrency(data.total) }}</strong>
            </b-list-group-item>
          </b-list-group>
        </b-col>

        <!-- Budget Analysis -->
        <b-col
          lg="6"
          md="12"
          class="mt-2"
        >
          <h5 class="mb-1">
            {{ $t('dashboard.budgetAnalysis') }}
          </h5>
          <b-card>
            <div class="d-flex justify-content-between mb-1">
              <span>{{ $t('dashboard.totalBudget') }}</span>
              <strong>{{ formatCurrency(financial.budget_analysis.total_budget) }}</strong>
            </div>
            <div class="d-flex justify-content-between mb-1">
              <span>{{ $t('dashboard.actualCost') }}</span>
              <strong>{{ formatCurrency(financial.budget_analysis.total_actual_cost) }}</strong>
            </div>
            <div class="d-flex justify-content-between mb-1">
              <span>{{ $t('dashboard.projectCount') }}</span>
              <strong>{{ financial.budget_analysis.project_count }}</strong>
            </div>
            <b-progress
              :value="financial.budget_analysis.utilization_percentage"
              :variant="getBudgetVariant(financial.budget_analysis.utilization_percentage)"
              class="mt-2"
              height="10px"
            />
          </b-card>
        </b-col>

        <!-- Recent Transactions -->
        <b-col
          cols="12"
          class="mt-2"
        >
          <h5 class="mb-1">
            {{ $t('dashboard.recentTransactions') }}
          </h5>
          <b-table
            :items="financial.recent_transactions"
            :fields="tableFields"
            responsive
            class="mb-0"
            small
            striped
            hover
          >
            <template #cell(number)="data">
              <b-link>
                {{ data.value }}
              </b-link>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="getStatusVariant(data.value)">
                {{ $t(`transaction.status.${data.value}`) }}
              </b-badge>
            </template>
            <template #cell(amount)="data">
              {{ formatCurrency(data.value) }}
            </template>
            <template #cell(transaction_date)="data">
              {{ formatDate(data.value) }}
            </template>
          </b-table>
        </b-col>
      </b-row>
    </b-card-body>

    <!-- Loading State -->
    <b-card-body v-else-if="isLoading">
      <div class="text-center py-3">
        <b-spinner variant="primary" />
      </div>
    </b-card-body>

    <!-- Empty State -->
    <b-card-body v-else>
      <div class="text-center py-3">
        <p class="text-muted">
          {{ $t('dashboard.noDataAvailable') }}
        </p>
      </div>
    </b-card-body>

    <!-- Custom Date Range Modal -->
    <b-modal
      v-model="showCustomDateModal"
      :title="$t('dashboard.customDateRange')"
      :ok-title="$t('common.apply')"
      :cancel-title="$t('common.cancel')"
      @ok="applyCustomDateRange"
      @hidden="resetCustomDateForm"
    >
      <b-form-group :label="$t('dashboard.startDate')">
        <b-form-input
          v-model="customStartDate"
          type="date"
        />
      </b-form-group>
      <b-form-group :label="$t('dashboard.endDate')">
        <b-form-input
          v-model="customEndDate"
          type="date"
        />
      </b-form-group>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BFormSelect,
  BTable,
  BBadge,
  BLink,
  BSpinner,
  BListGroup,
  BListGroupItem,
  BProgress,
  BModal,
  BFormGroup,
  BFormInput,
} from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'

export default {
  name: 'FinancialSummaryWidget',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BFormSelect,
    BTable,
    BBadge,
    BLink,
    BSpinner,
    BListGroup,
    BListGroupItem,
    BProgress,
    BModal,
    BFormGroup,
    BFormInput,
  },
  directives: {
    Ripple,
  },
  data() {
    const currentYear = new Date().getFullYear()
    return {
      selectedPeriod: 'month',
      selectedQuarter: null,
      selectedYear: currentYear,
      showCustomDateModal: false,
      customStartDate: '',
      customEndDate: '',
      periodOptions: [
        { value: 'day', text: this.$t('dashboard.period.day') },
        { value: 'week', text: this.$t('dashboard.period.week') },
        { value: 'month', text: this.$t('dashboard.period.month') },
        { value: 'quarter', text: this.$t('dashboard.period.quarter') },
        { value: 'year', text: this.$t('dashboard.period.year') },
        { value: 'all', text: this.$t('dashboard.period.all') },
      ],
      quarterOptions: [
        { value: 1, text: this.$t('dashboard.quarter.q1') },
        { value: 2, text: this.$t('dashboard.quarter.q2') },
        { value: 3, text: this.$t('dashboard.quarter.q3') },
        { value: 4, text: this.$t('dashboard.quarter.q4') },
      ],
      tableFields: [
        { key: 'number', label: this.$t('transaction.number') },
        { key: 'project_name', label: this.$t('transaction.project') },
        { key: 'customer_name', label: this.$t('transaction.customer') },
        { key: 'type', label: this.$t('transaction.type') },
        { key: 'amount', label: this.$t('transaction.amount') },
        { key: 'status', label: this.$t('transaction.status') },
        { key: 'transaction_date', label: this.$t('transaction.date') },
      ],
    }
  },
  computed: {
    ...mapGetters('dashboard', ['financial', 'loading', 'filters']),
    isLoading() {
      return this.loading.financial
    },
    showYearSelector() {
      return ['quarter', 'year'].includes(this.selectedPeriod)
    },
    yearOptions() {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let year = currentYear; year >= 2000; year--) {
        years.push({ value: year, text: year.toString() })
      }
      return years
    },
  },
  methods: {
    ...mapActions('dashboard', ['fetchFinancial', 'refreshWidget']),
    onPeriodChange() {
      if (this.selectedPeriod === 'quarter' && !this.selectedQuarter) {
        this.selectedQuarter = Math.ceil((new Date().getMonth() + 1) / 3)
      }
      this.onFilterChange()
    },
    onFilterChange() {
      const params = { period: this.selectedPeriod }

      if (this.selectedPeriod === 'quarter') {
        params.quarter = this.selectedQuarter
        params.year = this.selectedYear
      } else if (this.selectedPeriod === 'year') {
        params.year = this.selectedYear
      }

      this.fetchFinancial(params)
    },
    applyCustomDateRange() {
      if (this.customStartDate && this.customEndDate) {
        const params = {
          start_date: this.customStartDate,
          end_date: this.customEndDate,
        }
        this.fetchFinancial(params)
        this.selectedPeriod = 'month' // Reset to show custom range is active
      }
    },
    resetCustomDateForm() {
      this.customStartDate = ''
      this.customEndDate = ''
    },
    refreshData() {
      const params = { period: this.selectedPeriod }

      if (this.selectedPeriod === 'quarter') {
        params.quarter = this.selectedQuarter
        params.year = this.selectedYear
      } else if (this.selectedPeriod === 'year') {
        params.year = this.selectedYear
      }

      this.refreshWidget({ widget: 'financial', params })
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        approved: 'success',
        rejected: 'danger',
      }
      return variants[status] || 'secondary'
    },
    getBudgetVariant(percentage) {
      if (percentage < 50) return 'success'
      if (percentage < 75) return 'warning'
      return 'danger'
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('ar-EG', {
        style: 'currency',
        currency: 'EGP',
      }).format(value)
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('ar-EG')
    },
  },
  created() {
    this.selectedPeriod = this.filters.financialPeriod || 'month'
    this.selectedQuarter = this.filters.financialQuarter || Math.ceil((new Date().getMonth() + 1) / 3)
    this.selectedYear = this.filters.financialYear || new Date().getFullYear()
  },
}
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
